/**
 * How long the phone app has spent in the background, from the app's own
 * lifecycle (`appStateChange` from @capacitor/app), for upload telemetry.
 *
 * WHY NOT `visibilitychange`. That is what utils/foreground_clock.js counts,
 * and it answers a different question: was the PAGE stopped. Once
 * capacitor/upload_keep_awake.js keeps the page counted as on screen during a
 * video upload, the page is no longer hidden while the client is away, so a
 * visibility-based figure reads zero exactly when the app was in the
 * background. This one keeps saying whether the client left, which is what
 * tells "uploads now survive switching apps" apart from "nobody switched apps".
 *
 * Wall clock (Date.now), because the time the client was away is a wall-clock
 * fact, phone sleep included.
 *
 * No-op off the Capacitor shell: startAppBackgroundStopwatch() then returns
 * null and the events carry null. Never throws.
 */

import Honeybadger from "@honeybadger-io/js";
import { isCapacitorEnvironment } from "./detection";

let registered = false;
let listening = false;
let backgroundTotalMs = 0;
let backgroundSince = null;

function reportAppBackgroundError(error) {
  try {
    Honeybadger.notify(error, {
      context: { component: "capacitor_app_background_time" },
    });
  } catch {
    // reporting must never break the app
  }
}

export function handleAppStateChange({ isActive }) {
  const now = Date.now();
  if (!isActive) {
    if (backgroundSince === null) backgroundSince = now;
    return;
  }
  if (backgroundSince !== null) {
    backgroundTotalMs += now - backgroundSince;
    backgroundSince = null;
  }
}

const backgroundSoFar = () =>
  backgroundTotalMs +
  (backgroundSince === null ? 0 : Date.now() - backgroundSince);

/**
 * Start counting app-background time. Runs once; no-op off-shell.
 */
export function registerAppBackgroundTime() {
  if (!isCapacitorEnvironment() || registered) {
    return;
  }
  registered = true;
  import("@capacitor/app")
    .then(({ App }) => {
      try {
        const result = App.addListener("appStateChange", handleAppStateChange);
        listening = true;
        if (result && typeof result.catch === "function") {
          result.catch((error) => {
            // Nothing is counting after all: say "not measured" (null), not
            // "never left" (0).
            listening = false;
            registered = false; // allow a later call to retry
            reportAppBackgroundError(error);
          });
        }
      } catch (error) {
        registered = false; // allow a later call to retry
        reportAppBackgroundError(error);
      }
    })
    .catch((error) => {
      registered = false; // allow a later call to retry
      reportAppBackgroundError(error);
    });
}

/**
 * The returned function says how many whole milliseconds the app has spent in
 * the background since this was called. Null where nothing is counting (a
 * browser, or the listener could not be added), so telemetry can tell "not
 * measured" from "never left".
 * @returns {(() => number) | null}
 */
export function startAppBackgroundStopwatch() {
  if (!listening) return null;
  const atStart = backgroundSoFar();
  return () => Math.max(0, Math.round(backgroundSoFar() - atStart));
}

/** Test-only: forget all recorded state. */
export function __resetAppBackgroundTimeForTests({ listen = false } = {}) {
  registered = false;
  listening = listen;
  backgroundTotalMs = 0;
  backgroundSince = null;
}
